import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, categoriesApi } from "../api/client.js";
import AuthLayout from "../components/AuthLayout.jsx";
import { formatPrice, CATEGORIES } from "../data/products.js";
import { shortOrderId, paymentBadge } from "../api/client.js";
import Seo from "../components/Seo.jsx";
import PasswordInput from "../components/PasswordInput.jsx";
import { FarmLoaderOverlay } from "../components/FarmLoader.jsx";
import { BarTrend, AreaTrend, HBars, StatusDonut } from "../components/AdminCharts.jsx";
import WorldMap from "../components/WorldMap.jsx";

const KEY_STORE = "rf_admin_key";
// Owner/admin accounts are staff, not customers — excluded from user counts.
const ADMIN_EMAILS = new Set([
  "info@rimmonfarm.com",
  "info@rimmonfarm.com",
  "admin@rimmonfarm.com",
  "no-reply@rimmonfarm.com",
  "noreply@rimmonfarm.com",
]);
const isAdminUser = (u) => ADMIN_EMAILS.has(String(u?.email || "").toLowerCase().trim());
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "orders", label: "Orders" },
  { id: "payments", label: "Payments" },
  { id: "refunds", label: "Refunds" },
  { id: "users", label: "Users" },
  { id: "visitors", label: "Visitors" },
  { id: "categories", label: "Categories" },
];

const PAYMENT_FILTERS = [
  { id: "all", label: "All" },
  { id: "captured", label: "Captured" },
  { id: "pending", label: "Pending" },
  { id: "failed", label: "Failed" },
  { id: "refunded", label: "Refunded" },
  { id: "cancelled", label: "Cancelled" },
];

// Every order is one payment attempt. COD orders are payments too — they're
// just collected on the doorstep, so they sit in "pending" until delivered.
function toPaymentRow(o) {
  const meta = o.paymentMeta || {};
  const status = String(o.paymentStatus || "pending").toLowerCase();
  const bucket =
    status === "paid"
      ? "captured"
      : status === "refunded" || status === "refund_pending"
      ? "refunded"
      : status === "failed"
      ? "failed"
      // An order the auto-cancel sweep (or an admin) closed before the customer
      // ever paid stays paymentStatus "pending" forever — no capture/failure
      // event ever fires for a payment that was never made. Without this,
      // it shows the same "Pending / Awaiting payment" badge as a live order.
      : String(o.status).toLowerCase() === "cancelled"
      ? "cancelled"
      : "pending";
  return {
    orderId: o.id,
    paymentId: meta.razorpayPaymentId || null,
    gatewayOrderId: meta.razorpayOrderId || null,
    method: o.paymentMethod || "—",
    status,
    bucket,
    amount: Number(o.totalAmount) || 0,
    customer: o.customerName || "—",
    phone: o.customerPhone || null,
    deliveryAddress: o.deliveryAddress || null,
    createdOn: meta.paidAt || o.createdAt,
    orderStatus: o.status || "—",
    items: Array.isArray(o.items) ? o.items : [],
    // Detail-row extras.
    orderedOn: o.createdAt,
    paidAt: meta.paidAt || null,
    provider: meta.provider || (o.paymentMethod === "cod" ? "Cash on delivery" : null),
    refundId: meta.refundId || null,
    refundedAt: meta.refundedAt || null,
    refundError: meta.refundError || null,
    cancelledOn: o.cancelledAt || null,
    cancelReason: o.cancelReason || null,
  };
}

const PAYMENT_STATE_LABEL = {
  paid: "Captured",
  pending: "Pending",
  authorized: "Authorized",
  failed: "Failed",
  refunded: "Refunded",
  refund_pending: "Refund pending",
};
const PAYMENT_METHOD_LABEL = { online: "Online", cod: "Cash on delivery", card: "Card" };

// The fulfilment track, in order. Cancelling is a separate action — it has to
// run the refund + emails — so it isn't offered in this dropdown.
const ORDER_STATUSES = [
  { id: "pending", label: "Order placed" },
  { id: "confirmed", label: "Confirmed" },
  { id: "processing", label: "Being packed" },
  { id: "shipped", label: "Out for delivery" },
  { id: "delivered", label: "Delivered" },
];

const REFUND_FILTERS = [
  { id: "all", label: "All" },
  { id: "processing", label: "Processing" },
  { id: "processed", label: "Processed" },
  { id: "failed", label: "Failed" },
];

// A refund row is derived from the order it belongs to — we don't keep a
// separate refunds table. `refund_pending` with a stored refundError means
// Razorpay rejected the call and a human still owes the customer money.
function toRefundRow(o) {
  const meta = o.paymentMeta || {};
  const state = meta.refundError ? "failed" : o.paymentStatus === "refunded" ? "processed" : "processing";
  return {
    orderId: o.id,
    refundId: meta.refundId || null,
    paymentId: meta.razorpayPaymentId || null,
    createdOn: meta.refundedAt || meta.refundFailedAt || o.cancelledAt || o.createdAt,
    amount: Number(o.totalAmount) || 0,
    state,
    customer: o.customerName || "—",
    // Cancellation context, shown in the expandable detail row.
    phone: o.customerPhone || null,
    deliveryAddress: o.deliveryAddress || null,
    items: Array.isArray(o.items) ? o.items : [],
    orderedOn: o.createdAt,
    cancelledOn: o.cancelledAt,
    cancelledBy: o.cancelledBy || null,
    orderStatus: o.status || "—",
    paymentMethod: o.paymentMethod || "—",
    paymentStatus: o.paymentStatus || "—",
    refundStatus: meta.refundStatus || null,
    // Razorpay labels admin-side refunds "Business Initiated"; mirror that and
    // fall back to whatever reason the customer typed.
    reason: o.cancelReason || (o.cancelledBy === "admin" ? "Business Initiated" : "Customer cancelled"),
    error: meta.refundError || null,
  };
}

const REFUND_STATE_LABEL = {
  processed: "Refund Processed",
  processing: "Refund Processing",
  failed: "Refund Failed",
};

export default function Admin() {
  const [key, setKey] = useState(() => {
    try {
      return localStorage.getItem(KEY_STORE) || "";
    } catch {
      return "";
    }
  });
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");
  const [notice, setNotice] = useState("");
  const [data, setData] = useState({ stats: null, users: [], orders: [], visitors: null, categories: [] });
  const [refundFilter, setRefundFilter] = useState("all");
  const [refundQuery, setRefundQuery] = useState("");
  const [refundFrom, setRefundFrom] = useState("");
  const [refundTo, setRefundTo] = useState("");
  const [refundOpen, setRefundOpen] = useState(null);
  const [payFilter, setPayFilter] = useState("all");
  const [payQuery, setPayQuery] = useState("");
  const [payFrom, setPayFrom] = useState("");
  const [payTo, setPayTo] = useState("");
  const [payOpen, setPayOpen] = useState(null);
  const [orderOpen, setOrderOpen] = useState(null);

  const load = async (adminKey) => {
    setBusy(true);
    setError("");
    try {
      const [stats, users, orders, visitors, categoriesRes] = await Promise.all([
        adminApi.stats(adminKey),
        adminApi.users(adminKey),
        adminApi.orders(adminKey),
        adminApi.visitors(adminKey, 30),
        categoriesApi.list(),
      ]);
      setData({
        stats,
        users: users.users || [],
        orders: orders.orders || [],
        visitors,
        categories: categoriesRes.categories || [],
      });
      setAuthed(true);
      try {
        localStorage.setItem(KEY_STORE, adminKey);
      } catch {
        /* ignore */
      }
    } catch (e) {
      setError(e.message || "Access denied.");
      setAuthed(false);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    load(key.trim());
  };

  // Sign out: drop the stored key and every row it fetched, so nothing stays
  // on screen (or in localStorage) for the next person at this machine.
  const logout = () => {
    try {
      localStorage.removeItem(KEY_STORE);
    } catch {
      /* ignore */
    }
    setKey("");
    setAuthed(false);
    setData({ stats: null, users: [], orders: [], visitors: null, categories: [] });
    setTab("overview");
    setRefundOpen(null);
    setPayOpen(null);
    setNotice("");
    setError("");
  };

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3500);
  };

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load(key.trim());
      if (successMsg) flash(successMsg);
    } catch (e) {
      setError(e.message || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  // Admins can cancel at any stage — the API refunds a captured payment and
  // emails both the customer and the farm team.
  const cancelOrder = (o) => {
    const reason = window.prompt(
      `Cancel order ${shortOrderId(o.id)}?\n\nAny online payment is refunded automatically and the customer is emailed.\n\nReason (optional):`,
      ""
    );
    if (reason === null) return;
    runAction(
      () => adminApi.cancelOrder(key.trim(), o.id, reason.trim() || undefined),
      "Order cancelled — customer and team notified."
    );
  };
  const changeStatus = (o, status) => {
    if (status === (o.status || "pending")) return;
    const label = ORDER_STATUSES.find((s) => s.id === status)?.label || status;
    runAction(
      () => adminApi.setOrderStatus(key.trim(), o.id, status),
      `${shortOrderId(o.id)} marked "${label}".`
    );
  };
  const deleteOrder = (o) => {
    if (!window.confirm(`Delete order ${shortOrderId(o.id)}? This cannot be undone.`)) return;
    runAction(() => adminApi.deleteOrder(key.trim(), o.id), "Order deleted.");
  };
  const clearOrders = () => {
    if (!window.confirm("Delete ALL orders permanently? This cannot be undone.")) return;
    runAction(() => adminApi.clearOrders(key.trim()), "All orders cleared.");
  };
  const deleteUser = (u) => {
    if (!window.confirm(`Delete user ${u.email}? Their addresses & cards are removed too. This cannot be undone.`)) return;
    runAction(() => adminApi.deleteUser(key.trim(), u.id), "User deleted.");
  };
  const clearUsers = () => {
    if (!window.confirm("Delete ALL users permanently? This cannot be undone.")) return;
    runAction(() => adminApi.clearUsers(key.trim()), "All users cleared.");
  };
  const toggleBlock = (u) => {
    const next = !u.blocked;
    if (!window.confirm(`${next ? "Block" : "Unblock"} ${u.email} from placing orders?`)) return;
    runAction(() => adminApi.setBlocked(key.trim(), u.id, next), next ? "User blocked." : "User unblocked.");
  };
  // Just hides/shows the category's filter chip on the Products page — the
  // products themselves stay in the catalog either way, so no confirm needed.
  const toggleCategory = (c) => {
    const next = !c.enabled;
    runAction(
      () => adminApi.setCategoryEnabled(key.trim(), c.id, next),
      next ? `"${c.label}" shown on the shop page.` : `"${c.label}" hidden from the shop page.`
    );
  };

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const formatAddress = (a) => {
    if (!a) return "";
    return [a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(", ");
  };

  if (!authed) {
    return (
      <AuthLayout
        eyebrow="Staff only"
        title="Admin sign in"
        subtitle="Enter your admin key to open the Rimmon Farm dashboard."
        aside={{
          heading: "Rimmon Farm control room",
          perks: [
            "Live revenue, orders & customers",
            "Cancel orders and issue refunds",
            "Track refunds end to end",
            "Visitor analytics across India",
          ],
        }}
      >
        <Seo title="Admin" path="/admin" noindex />
        <FarmLoaderOverlay show={busy} message="Checking key…" />
        <form onSubmit={submit} className="auth__form">
          {error && <p className="auth__error">{error}</p>}
          <PasswordInput
            label="Admin key"
            name="adminKey"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
            placeholder="Enter admin key"
          />
          <button className="btn btn--primary btn--block btn--lg" disabled={busy}>
            {busy ? "Checking…" : "Unlock dashboard"}
          </button>
        </form>
        <p className="auth__switch">
          Not staff? <Link to="/">Back to the shop</Link>
        </p>
      </AuthLayout>
    );
  }

  const s = data.stats || {};
  const v = data.visitors || {};
  const revenue = Math.round(s.totalRevenue ?? 0);
  const isPaid = (o) => o.paymentStatus === "paid" || o.status === "paid";
  const paidOrders = data.orders.filter(isPaid).length;
  const paidRevenue = Math.round(
    data.orders.filter(isPaid).reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0)
  );

  // Server only tracks id + enabled; the label comes from the same CATEGORIES
  // list the storefront uses, so this always matches what customers see.
  const categoryRows = data.categories.map((c) => ({
    ...c,
    label: CATEGORIES.find((cat) => cat.id === c.id)?.label || c.id,
  }));

  // Admins are staff, not customers — exclude from the user list & counts.
  const customers = data.users.filter((u) => !isAdminUser(u));
  const customerCount = customers.length;
  const blockedCount = customers.filter((u) => u.blocked).length;
  const avgOrder = data.orders.length ? Math.round(revenue / data.orders.length) : 0;

  const kpis = [
    { label: "Total revenue", value: formatPrice(revenue), tone: "green" },
    { label: "Paid revenue", value: formatPrice(paidRevenue), tone: "blue" },
    { label: "Total orders", value: s.orderCount ?? data.orders.length, tone: "violet" },
    { label: "Avg order value", value: formatPrice(avgOrder), tone: "amber" },
  ];

  const summary = [
    { label: "Customers", value: customerCount, sub: `${paidOrders} paid orders`, tone: "green", icon: "◕" },
    { label: "Blocked", value: blockedCount, sub: `${customerCount} customers`, tone: "red", icon: "⦸" },
    { label: "Visits today", value: v.viewsToday ?? s.visitsToday ?? 0, sub: `${v.uniqueVisitors ?? 0} unique / ${v.days ?? 30}d`, tone: "violet", icon: "◔" },
  ];

  const byLocation = v.byLocation || [];
  const byPage = v.byPage || [];
  const bySource = v.bySource || [];
  const locName = (l) => [l.city, l.region, l.countryName].filter(Boolean).join(", ") || "Unknown";

  // --- Derived chart data ---
  // Orders & revenue over the last 14 days (from the order list).
  const DAYS = 14;
  const dayBuckets = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = DAYS - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    dayBuckets.push({ date: d, key: d.toDateString(), orders: 0, revenue: 0 });
  }
  const bucketByKey = new Map(dayBuckets.map((b) => [b.key, b]));
  for (const o of data.orders) {
    if (!o.createdAt) continue;
    const d = new Date(o.createdAt);
    d.setHours(0, 0, 0, 0);
    const b = bucketByKey.get(d.toDateString());
    if (b) {
      b.orders += 1;
      b.revenue += Number(o.totalAmount) || 0;
    }
  }
  const dayLabel = (d) => `${d.getDate()}/${d.getMonth() + 1}`;
  const ordersSeries = dayBuckets.map((b) => ({ label: dayLabel(b.date), value: b.orders }));
  const revenueSeries = dayBuckets.map((b) => ({ label: dayLabel(b.date), value: Math.round(b.revenue) }));

  // Order status breakdown for the donut.
  const statusMap = new Map();
  for (const o of data.orders) {
    const k = String(o.status || "pending").toLowerCase();
    statusMap.set(k, (statusMap.get(k) || 0) + 1);
  }
  const statusSeries = [...statusMap.entries()].map(([label, value]) => ({ label, value }));

  // Top locations / pages / sources as horizontal bars.
  const topLocations = byLocation.slice(0, 8).map((l) => ({ label: locName(l), value: l.views }));
  const topPages = byPage.slice(0, 8).map((p) => ({ label: p.path, value: p.views }));
  const topSources = bySource.slice(0, 6).map((srow) => ({ label: srow.source, value: srow.views }));

  // Visitors tab — daily/hourly trend and the returning-vs-new split all come
  // pre-computed (and zero-filled) from the API; just cap the live feed here.
  const byDay = v.byDay || [];
  const byHour = v.byHour || [];
  const returningDonut = [
    { label: "Returning", value: v.returningVisitors || 0 },
    { label: "One-time", value: v.oneTimeVisitors || 0 },
  ];
  const recentVisits = (v.recent || []).slice(0, 8);
  const timeAgo = (iso) => {
    if (!iso) return "";
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr ago`;
    return fmtDate(iso);
  };

  // --- Payments ------------------------------------------------------------
  const allPayments = data.orders
    .map(toPaymentRow)
    .sort((a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0));

  const payTotals = allPayments.reduce(
    (acc, p) => {
      acc[p.bucket].amount += p.amount;
      acc[p.bucket].count += 1;
      return acc;
    },
    {
      captured: { amount: 0, count: 0 },
      pending: { amount: 0, count: 0 },
      failed: { amount: 0, count: 0 },
      refunded: { amount: 0, count: 0 },
      cancelled: { amount: 0, count: 0 },
    }
  );

  const paySearch = payQuery.trim().toLowerCase();
  const payments = allPayments.filter((p) => {
    if (payFilter !== "all" && p.bucket !== payFilter) return false;
    if (payFrom && new Date(p.createdOn) < new Date(`${payFrom}T00:00:00`)) return false;
    if (payTo && new Date(p.createdOn) > new Date(`${payTo}T23:59:59`)) return false;
    if (!paySearch) return true;
    return [p.paymentId, p.gatewayOrderId, p.orderId, p.customer, p.phone]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(paySearch));
  });

  const payFiltered = Boolean(paySearch || payFrom || payTo || payFilter !== "all");
  const clearPayFilters = () => {
    setPayFilter("all");
    setPayQuery("");
    setPayFrom("");
    setPayTo("");
  };

  // --- Refunds -------------------------------------------------------------
  const allRefunds = data.orders
    .filter((o) => o.paymentStatus === "refunded" || o.paymentStatus === "refund_pending")
    .map(toRefundRow)
    .sort((a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0));

  const refundTotals = allRefunds.reduce(
    (acc, r) => {
      acc[r.state].amount += r.amount;
      acc[r.state].count += 1;
      return acc;
    },
    {
      processed: { amount: 0, count: 0 },
      processing: { amount: 0, count: 0 },
      failed: { amount: 0, count: 0 },
    }
  );

  const refundSearch = refundQuery.trim().toLowerCase();
  const refunds = allRefunds.filter((r) => {
    if (refundFilter !== "all" && r.state !== refundFilter) return false;
    if (refundFrom && new Date(r.createdOn) < new Date(`${refundFrom}T00:00:00`)) return false;
    if (refundTo && new Date(r.createdOn) > new Date(`${refundTo}T23:59:59`)) return false;
    if (!refundSearch) return true;
    return [r.refundId, r.paymentId, r.orderId, r.customer]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(refundSearch));
  });

  const refundFiltered = Boolean(refundSearch || refundFrom || refundTo || refundFilter !== "all");
  const clearRefundFilters = () => {
    setRefundFilter("all");
    setRefundQuery("");
    setRefundFrom("");
    setRefundTo("");
  };

  return (
    <section className="rzp">
      <Seo title="Admin Dashboard" path="/admin" noindex />
      <FarmLoaderOverlay show={busy} message="Working…" />
      <div className="container">
        <div className="rzp-topbar">
          <div>
            <h1 className="rzp-title">Dashboard</h1>
            <p className="rzp-sub">Rimmon Farm — live revenue, orders, users &amp; traffic.</p>
          </div>
          <div className="rzp-topbar-actions">
            <Link
              to="/"
              className="rzp-btn rzp-btn--ghost rzp-btn--icon"
              title="Back to site"
              aria-label="Back to site"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                   strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M19 12H5" />
                <path d="m12 19-7-7 7-7" />
              </svg>
            </Link>
            <button
              className="rzp-btn rzp-btn--ghost rzp-btn--icon"
              onClick={() => load(key)}
              disabled={busy}
              title={busy ? "Refreshing…" : "Refresh"}
              aria-label={busy ? "Refreshing" : "Refresh"}
            >
              <svg className={busy ? "rzp-spin" : undefined}
                   viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                   strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-3.5-7.1" />
                <path d="M21 3v6h-6" />
              </svg>
            </button>
            <button
              className="rzp-btn rzp-btn--logout rzp-btn--icon"
              onClick={logout}
              disabled={busy}
              title="Sign out"
              aria-label="Sign out"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                   strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="m16 17 5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
            </button>
          </div>
        </div>

        {notice && <div className="rzp-toast">{notice}</div>}
        {error && <div className="rzp-toast rzp-toast--err">{error}</div>}

        <div className="rzp-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`rzp-tab ${tab === t.id ? "is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Headline numbers belong to Overview only — the other tabs are
            working screens and shouldn't repeat them. */}
        {tab === "overview" && (
          <>
            <div className="rzp-kpis">
              {kpis.map((k) => (
                <div className={`rzp-kpi rzp-kpi--${k.tone}`} key={k.label}>
                  <span className="rzp-kpi-label">{k.label}</span>
                  <strong className="rzp-kpi-value">{k.value}</strong>
                </div>
              ))}
            </div>

            <div className="rzp-summary">
              {summary.map((c) => (
                <div className={`rzp-card rzp-card--${c.tone}`} key={c.label}>
                  <div className="rzp-card-top">
                    <span className="rzp-card-label">{c.label}</span>
                    <span className="rzp-card-icon">{c.icon}</span>
                  </div>
                  <strong className="rzp-card-value">{c.value}</strong>
                  <span className="rzp-card-sub">{c.sub}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "overview" && (
          <>
            <div className="rzp-charts">
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Orders · last 14 days</h2></div>
                <BarTrend data={ordersSeries} title="Orders per day" />
              </div>
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Revenue · last 14 days</h2></div>
                <AreaTrend data={revenueSeries} format={(x) => `₹${(x / 1000).toFixed(x >= 1000 ? 0 : 1)}k`} title="Revenue per day" />
              </div>
            </div>
            <div className="rzp-charts rzp-charts--split">
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Order status</h2></div>
                <StatusDonut data={statusSeries} />
              </div>
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Where visitors are</h2></div>
                <div className="rzp-map-layout">
                  <WorldMap locations={byLocation} />
                  <div className="rzp-map-side">
                    <h3 className="rzp-mini-title">Top locations</h3>
                    <HBars data={topLocations} />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {(tab === "overview" || tab === "orders") && (
          <div className="rzp-panel">
            <div className="rzp-panel-head">
              <h2>{tab === "overview" ? "Recent orders" : "All orders"}</h2>
              <button className="rzp-btn rzp-btn--danger" onClick={clearOrders} disabled={busy || !data.orders.length}>
                Clear all orders
              </button>
            </div>
            <div className="rzp-table-wrap">
              <table className="rzp-table">
                <thead>
                  <tr><th>Order</th><th>Customer</th><th>Items</th><th>Total</th><th>Payment</th><th>Status</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {data.orders.length === 0 && <tr><td colSpan={8} className="rzp-empty">No orders yet.</td></tr>}
                  {(tab === "overview" ? data.orders.slice(0, 10) : data.orders).map((o) => {
                    const pay = paymentBadge(o);
                    const addrText = formatAddress(o.deliveryAddress);
                    const open = orderOpen === o.id;
                    return (
                    <Fragment key={o.id}>
                    <tr>
                      <td className="rzp-mono">{shortOrderId(o.id)}</td>
                      <td>{o.customerName || "—"}<br /><small>{o.customerPhone || ""}</small></td>
                      <td>{(o.items || []).map((i) => `${i.name}×${i.quantity}`).join(", ")}</td>
                      <td>{formatPrice(Math.round(Number(o.totalAmount) || 0))}</td>
                      <td><span className={`order__badge order__badge--${pay.tone}`}>{pay.text}</span></td>
                      <td>
                        {String(o.status).toLowerCase() === "cancelled" ? (
                          <span className="rzp-badge rzp-badge--cancelled">cancelled</span>
                        ) : (
                          <select
                            className="rzp-select"
                            value={o.status || "pending"}
                            onChange={(e) => changeStatus(o, e.target.value)}
                            disabled={busy}
                            aria-label={`Status for order ${shortOrderId(o.id)}`}
                          >
                            {ORDER_STATUSES.map((s) => (
                              <option key={s.id} value={s.id}>{s.label}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>{fmtDate(o.createdAt)}</td>
                      <td className="rzp-actions">
                        <button className="rzp-link" onClick={() => setOrderOpen(open ? null : o.id)}>
                          {open ? "Hide ▲" : "Details ›"}
                        </button>
                        {String(o.status || "").toLowerCase() !== "cancelled" && (
                          <button className="rzp-link-warn" onClick={() => cancelOrder(o)} disabled={busy}>Cancel</button>
                        )}
                        <button className="rzp-link-danger" onClick={() => deleteOrder(o)} disabled={busy}>Delete</button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="rfx-detail-row">
                        <td colSpan={8}>
                          <div className="rfx-detail">
                            <div className="rfx-detail-col">
                              <h4>Customer</h4>
                              <dl>
                                <div><dt>Name</dt><dd>{o.customerName || "—"}</dd></div>
                                <div><dt>Phone</dt><dd>{o.customerPhone || "—"}</dd></div>
                              </dl>
                            </div>
                            <div className="rfx-detail-col">
                              <h4>Delivery address</h4>
                              {o.deliveryAddress ? (
                                <dl>
                                  {o.deliveryAddress.label && <div><dt>Label</dt><dd>{o.deliveryAddress.label}</dd></div>}
                                  <div><dt>Line 1</dt><dd>{o.deliveryAddress.line1 || "—"}</dd></div>
                                  {o.deliveryAddress.line2 && <div><dt>Line 2</dt><dd>{o.deliveryAddress.line2}</dd></div>}
                                  <div><dt>City</dt><dd>{o.deliveryAddress.city || "—"}</dd></div>
                                  <div><dt>State</dt><dd>{o.deliveryAddress.state || "—"}</dd></div>
                                  <div><dt>Pincode</dt><dd>{o.deliveryAddress.pincode || "—"}</dd></div>
                                </dl>
                              ) : (
                                <p className="rfx-muted rfx-detail-note">No delivery address on this order.</p>
                              )}
                            </div>
                            <div className="rfx-detail-col rfx-detail-col--items">
                              <h4>Items</h4>
                              <ul className="rfx-items">
                                {(o.items || []).length === 0 && <li className="rfx-muted">No line items recorded.</li>}
                                {(o.items || []).map((i, idx) => (
                                  <li key={`${o.id}-${i.productId || i.id || idx}`}>
                                    <span>{i.quantity} {i.unit || "kg"} × {i.name}</span>
                                    <strong>{formatPrice(Math.round(Number(i.lineTotal) || 0))}</strong>
                                  </li>
                                ))}
                                <li className="rfx-items-total">
                                  <span>Order total</span>
                                  <strong>{formatPrice(Math.round(Number(o.totalAmount) || 0))}</strong>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "payments" && (
          <>
            <div className="rfx-tiles rfx-tiles--4">
              <div className="rfx-tile">
                <span className="rfx-tile-label">Captured</span>
                <strong className="rfx-tile-value">{formatPrice(Math.round(payTotals.captured.amount))}</strong>
                <span className="rfx-tile-sub">from {payTotals.captured.count} payment{payTotals.captured.count === 1 ? "" : "s"}</span>
              </div>
              <div className="rfx-tile">
                <span className="rfx-tile-label">Pending</span>
                <strong className="rfx-tile-value">{formatPrice(Math.round(payTotals.pending.amount))}</strong>
                <span className="rfx-tile-sub">{payTotals.pending.count} awaiting payment</span>
              </div>
              <div className="rfx-tile">
                <span className="rfx-tile-label">Failed</span>
                <strong className="rfx-tile-value">{formatPrice(Math.round(payTotals.failed.amount))}</strong>
                <span className="rfx-tile-sub">from {payTotals.failed.count} attempt{payTotals.failed.count === 1 ? "" : "s"}</span>
              </div>
              <div className="rfx-tile">
                <span className="rfx-tile-label">Refunded</span>
                <strong className="rfx-tile-value">{formatPrice(Math.round(payTotals.refunded.amount))}</strong>
                <span className="rfx-tile-sub">from {payTotals.refunded.count} payment{payTotals.refunded.count === 1 ? "" : "s"}</span>
              </div>
            </div>

            <div className="rzp-panel">
              <div className="rfx-filters">
                <div className="rfx-segment">
                  {PAYMENT_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      className={`rfx-seg-btn ${payFilter === f.id ? "is-active" : ""}`}
                      onClick={() => setPayFilter(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <input
                  className="rfx-search"
                  type="search"
                  value={payQuery}
                  onChange={(e) => setPayQuery(e.target.value)}
                  placeholder="Search payment ID, order, customer or phone"
                />
              </div>
              <div className="rfx-filters rfx-filters--dates">
                <label className="rfx-date">
                  <span>From</span>
                  <input type="date" value={payFrom} onChange={(e) => setPayFrom(e.target.value)} />
                </label>
                <label className="rfx-date">
                  <span>To</span>
                  <input type="date" value={payTo} onChange={(e) => setPayTo(e.target.value)} />
                </label>
                {payFiltered && <button className="rzp-link" onClick={clearPayFilters}>Clear filter</button>}
                <span className="rfx-count">
                  {payments.length} of {allPayments.length} payment{allPayments.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="rzp-table-wrap">
                <table className="rzp-table">
                  <thead>
                    <tr>
                      <th>Payment ID</th><th>Order</th><th>Customer</th><th>Method</th>
                      <th>Created on</th><th>Amount</th><th>Status</th><th>Items</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 && (
                      <tr>
                        <td colSpan={9} className="rzp-empty">
                          {allPayments.length ? "No payments match these filters." : "No payments yet."}
                        </td>
                      </tr>
                    )}
                    {payments.map((p) => {
                      const open = payOpen === p.orderId;
                      return (
                      <Fragment key={p.orderId}>
                      <tr>
                        <td className="rzp-mono">
                          {p.paymentId || "—"}
                          {p.gatewayOrderId && <><br /><small className="rfx-muted">{p.gatewayOrderId}</small></>}
                        </td>
                        <td className="rzp-mono">{shortOrderId(p.orderId)}</td>
                        <td>{p.customer}{p.phone && <><br /><small>{p.phone}</small></>}</td>
                        <td>{PAYMENT_METHOD_LABEL[p.method] || p.method}</td>
                        <td>{fmtDate(p.createdOn)}</td>
                        <td>{formatPrice(Math.round(p.amount))}</td>
                        <td>
                          <span className={`rfx-badge rfx-badge--pay-${p.bucket}`}>
                            {p.bucket === "cancelled" ? "Cancelled" : PAYMENT_STATE_LABEL[p.status] || p.status}
                          </span>
                        </td>
                        <td>{p.items.map((i) => `${i.name}×${i.quantity}`).join(", ") || "—"}</td>
                        <td>
                          <button className="rzp-link" onClick={() => setPayOpen(open ? null : p.orderId)}>
                            {open ? "Hide ▲" : "Details ›"}
                          </button>
                        </td>
                      </tr>
                      {open && (
                        <tr className="rfx-detail-row">
                          <td colSpan={9}>
                            <div className="rfx-detail">
                              <div className="rfx-detail-col">
                                <h4>Payment</h4>
                                <dl>
                                  <div><dt>Payment ID</dt><dd className="rzp-mono">{p.paymentId || "—"}</dd></div>
                                  <div><dt>Gateway order ID</dt><dd className="rzp-mono">{p.gatewayOrderId || "—"}</dd></div>
                                  <div><dt>Provider</dt><dd>{p.provider || "—"}</dd></div>
                                  <div><dt>Method</dt><dd>{PAYMENT_METHOD_LABEL[p.method] || p.method}</dd></div>
                                  <div><dt>Status</dt><dd>{PAYMENT_STATE_LABEL[p.status] || p.status}</dd></div>
                                  <div><dt>Amount</dt><dd>{formatPrice(Math.round(p.amount))}</dd></div>
                                </dl>
                              </div>
                              <div className="rfx-detail-col">
                                <h4>Order</h4>
                                <dl>
                                  <div><dt>Order ID</dt><dd className="rzp-mono">{shortOrderId(p.orderId)}</dd></div>
                                  <div><dt>Ordered on</dt><dd>{fmtDate(p.orderedOn)}</dd></div>
                                  <div><dt>Paid at</dt><dd>{p.paidAt ? fmtDate(p.paidAt) : "—"}</dd></div>
                                  <div><dt>Order status</dt><dd>{p.orderStatus}</dd></div>
                                  <div><dt>Customer</dt><dd>{p.customer}{p.phone ? ` · ${p.phone}` : ""}</dd></div>
                                  <div><dt>Delivery address</dt><dd>{formatAddress(p.deliveryAddress) || "—"}</dd></div>
                                </dl>
                              </div>
                              <div className="rfx-detail-col">
                                <h4>{p.bucket === "refunded" ? "Refund" : "Settlement"}</h4>
                                {p.bucket === "refunded" ? (
                                  <>
                                    <dl>
                                      <div><dt>Refund ID</dt><dd className="rzp-mono">{p.refundId || "—"}</dd></div>
                                      <div><dt>Refunded at</dt><dd>{p.refundedAt ? fmtDate(p.refundedAt) : "—"}</dd></div>
                                      <div><dt>Cancelled on</dt><dd>{p.cancelledOn ? fmtDate(p.cancelledOn) : "—"}</dd></div>
                                      <div><dt>Reason</dt><dd>{p.cancelReason || "—"}</dd></div>
                                    </dl>
                                    {p.refundError && <p className="rfx-err rfx-detail-err">{p.refundError}</p>}
                                  </>
                                ) : (
                                  <p className="rfx-muted rfx-detail-note">
                                    {p.bucket === "captured"
                                      ? "Captured by Razorpay and due in the next settlement cycle."
                                      : p.bucket === "failed"
                                      ? "The payment attempt failed — nothing was captured."
                                      : p.bucket === "cancelled"
                                      ? "Cancelled before the customer paid — nothing was captured, no refund needed."
                                      : p.method === "cod"
                                      ? "Cash on delivery — collect on the doorstep."
                                      : "Awaiting payment from the customer."}
                                  </p>
                                )}
                              </div>
                              <div className="rfx-detail-col rfx-detail-col--items">
                                <h4>Items</h4>
                                <ul className="rfx-items">
                                  {p.items.length === 0 && <li className="rfx-muted">No line items recorded.</li>}
                                  {p.items.map((i, idx) => (
                                    <li key={`${p.orderId}-${i.productId || i.id || idx}`}>
                                      <span>{i.quantity} {i.unit || "kg"} × {i.name}</span>
                                      <strong>{formatPrice(Math.round(Number(i.lineTotal) || 0))}</strong>
                                    </li>
                                  ))}
                                  <li className="rfx-items-total">
                                    <span>Order total</span>
                                    <strong>{formatPrice(Math.round(p.amount))}</strong>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "refunds" && (
          <>
            <div className="rfx-tiles">
              <div className="rfx-tile">
                <span className="rfx-tile-label">Refunded</span>
                <strong className="rfx-tile-value">{formatPrice(Math.round(refundTotals.processed.amount))}</strong>
                <span className="rfx-tile-sub">from {refundTotals.processed.count} processed refund{refundTotals.processed.count === 1 ? "" : "s"}</span>
              </div>
              <div className="rfx-tile">
                <span className="rfx-tile-label">Processing</span>
                <strong className="rfx-tile-value">{formatPrice(Math.round(refundTotals.processing.amount))}</strong>
                <span className="rfx-tile-sub">from {refundTotals.processing.count} refund{refundTotals.processing.count === 1 ? "" : "s"}</span>
              </div>
              <div className="rfx-tile">
                <span className="rfx-tile-label">Failed</span>
                <strong className="rfx-tile-value">{formatPrice(Math.round(refundTotals.failed.amount))}</strong>
                <span className="rfx-tile-sub">
                  {refundTotals.failed.count
                    ? `${refundTotals.failed.count} need manual settlement`
                    : "from 0 refunds"}
                </span>
              </div>
            </div>

            <div className="rzp-panel">
              <div className="rfx-filters">
                <div className="rfx-segment">
                  {REFUND_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      className={`rfx-seg-btn ${refundFilter === f.id ? "is-active" : ""}`}
                      onClick={() => setRefundFilter(f.id)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <input
                  className="rfx-search"
                  type="search"
                  value={refundQuery}
                  onChange={(e) => setRefundQuery(e.target.value)}
                  placeholder="Search refund ID, payment ID, order or customer"
                />
              </div>
              <div className="rfx-filters rfx-filters--dates">
                <label className="rfx-date">
                  <span>From</span>
                  <input type="date" value={refundFrom} onChange={(e) => setRefundFrom(e.target.value)} />
                </label>
                <label className="rfx-date">
                  <span>To</span>
                  <input type="date" value={refundTo} onChange={(e) => setRefundTo(e.target.value)} />
                </label>
                {refundFiltered && (
                  <button className="rzp-link" onClick={clearRefundFilters}>Clear filter</button>
                )}
                <span className="rfx-count">
                  {refunds.length} of {allRefunds.length} refund{allRefunds.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="rzp-table-wrap">
                <table className="rzp-table">
                  <thead>
                    <tr>
                      <th>Refund ID</th><th>Payment ID</th><th>Order</th><th>Customer</th>
                      <th>Created on</th><th>Amount</th><th>Status</th><th>Refund Source/Reason</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {refunds.length === 0 && (
                      <tr>
                        <td colSpan={9} className="rzp-empty">
                          {allRefunds.length ? "No refunds match these filters." : "No refunds yet."}
                        </td>
                      </tr>
                    )}
                    {refunds.map((r) => {
                      const open = refundOpen === r.orderId;
                      return (
                      <Fragment key={r.orderId}>
                      <tr>
                        <td className="rzp-mono">{r.refundId || "—"}</td>
                        <td className="rzp-mono">{r.paymentId || "—"}</td>
                        <td className="rzp-mono">{shortOrderId(r.orderId)}</td>
                        <td>{r.customer}</td>
                        <td>{fmtDate(r.createdOn)}</td>
                        <td>{formatPrice(Math.round(r.amount))}</td>
                        <td>
                          <span className={`rfx-badge rfx-badge--${r.state}`}>{REFUND_STATE_LABEL[r.state]}</span>
                        </td>
                        <td>
                          {r.reason}
                          {r.error && <><br /><small className="rfx-err">{r.error}</small></>}
                        </td>
                        <td>
                          <button className="rzp-link" onClick={() => setRefundOpen(open ? null : r.orderId)}>
                            {open ? "Hide ▲" : "Details ›"}
                          </button>
                        </td>
                      </tr>
                      {open && (
                        <tr className="rfx-detail-row">
                          <td colSpan={9}>
                            <div className="rfx-detail">
                              <div className="rfx-detail-col">
                                <h4>Cancellation</h4>
                                <dl>
                                  <div><dt>Cancelled on</dt><dd>{fmtDate(r.cancelledOn)}</dd></div>
                                  <div><dt>Cancelled by</dt><dd>{r.cancelledBy === "admin" ? "Admin / farm team" : r.cancelledBy === "customer" ? "Customer" : "—"}</dd></div>
                                  <div><dt>Reason</dt><dd>{r.reason}</dd></div>
                                  <div><dt>Order status</dt><dd>{r.orderStatus}</dd></div>
                                </dl>
                              </div>
                              <div className="rfx-detail-col">
                                <h4>Order</h4>
                                <dl>
                                  <div><dt>Order ID</dt><dd className="rzp-mono">{shortOrderId(r.orderId)}</dd></div>
                                  <div><dt>Ordered on</dt><dd>{fmtDate(r.orderedOn)}</dd></div>
                                  <div><dt>Customer</dt><dd>{r.customer}{r.phone ? ` · ${r.phone}` : ""}</dd></div>
                                  <div><dt>Delivery address</dt><dd>{formatAddress(r.deliveryAddress) || "—"}</dd></div>
                                  <div><dt>Payment</dt><dd>{r.paymentMethod} / {r.paymentStatus}</dd></div>
                                </dl>
                              </div>
                              <div className="rfx-detail-col">
                                <h4>Refund</h4>
                                <dl>
                                  <div><dt>Refund ID</dt><dd className="rzp-mono">{r.refundId || "—"}</dd></div>
                                  <div><dt>Payment ID</dt><dd className="rzp-mono">{r.paymentId || "—"}</dd></div>
                                  <div><dt>Amount</dt><dd>{formatPrice(Math.round(r.amount))}</dd></div>
                                  <div><dt>Gateway status</dt><dd>{r.refundStatus || (r.state === "failed" ? "failed" : "—")}</dd></div>
                                </dl>
                                {r.error && <p className="rfx-err rfx-detail-err">{r.error}</p>}
                              </div>
                              <div className="rfx-detail-col rfx-detail-col--items">
                                <h4>Cancelled items</h4>
                                <ul className="rfx-items">
                                  {r.items.length === 0 && <li className="rfx-muted">No line items recorded.</li>}
                                  {r.items.map((i, idx) => (
                                    <li key={`${r.orderId}-${i.productId || i.id || idx}`}>
                                      <span>{i.quantity} {i.unit || "kg"} × {i.name}</span>
                                      <strong>{formatPrice(Math.round(Number(i.lineTotal) || 0))}</strong>
                                    </li>
                                  ))}
                                  <li className="rfx-items-total">
                                    <span>Order total</span>
                                    <strong>{formatPrice(Math.round(r.amount))}</strong>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "users" && (
          <div className="rzp-panel">
            <div className="rzp-panel-head">
              <h2>Customers <span className="rzp-count">{customerCount}</span></h2>
              <button className="rzp-btn rzp-btn--danger" onClick={clearUsers} disabled={busy || !customers.length}>
                Clear all users
              </button>
            </div>
            <div className="rzp-table-wrap">
              <table className="rzp-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Phone</th><th>Orders</th><th>Status</th><th>Joined</th><th></th></tr>
                </thead>
                <tbody>
                  {customers.length === 0 && <tr><td colSpan={7} className="rzp-empty">No customers yet.</td></tr>}
                  {customers.map((u) => (
                    <tr key={u.id || u.email} className={u.blocked ? "is-blocked" : ""}>
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td>{u.phone || "—"}</td>
                      <td>{u.orderCount ?? 0}</td>
                      <td>
                        {u.blocked
                          ? <span className="rzp-badge rzp-badge--failed">Blocked</span>
                          : <span className="rzp-badge rzp-badge--paid">Active</span>}
                      </td>
                      <td>{fmtDate(u.createdAt)}</td>
                      <td className="rzp-actions">
                        <button className="rzp-link" onClick={() => toggleBlock(u)} disabled={busy}>
                          {u.blocked ? "Unblock" : "Block"}
                        </button>
                        <button className="rzp-link-danger" onClick={() => deleteUser(u)} disabled={busy}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "visitors" && (
          <>
            <div className="rzp-kpis">
              <div className="rzp-kpi rzp-kpi--violet">
                <span className="rzp-kpi-label">Today's visitors</span>
                <strong className="rzp-kpi-value">{v.viewsToday ?? 0}</strong>
              </div>
              <div className="rzp-kpi rzp-kpi--blue">
                <span className="rzp-kpi-label">Unique today</span>
                <strong className="rzp-kpi-value">{v.uniqueToday ?? 0}</strong>
              </div>
              <div className="rzp-kpi rzp-kpi--green">
                <span className="rzp-kpi-label">Total visits ({v.days ?? 30}d)</span>
                <strong className="rzp-kpi-value">{v.totalViews ?? 0}</strong>
              </div>
              <div className="rzp-kpi rzp-kpi--amber">
                <span className="rzp-kpi-label">Unique visitors ({v.days ?? 30}d)</span>
                <strong className="rzp-kpi-value">{v.uniqueVisitors ?? 0}</strong>
              </div>
            </div>

            <div className="rzp-charts rzp-charts--split">
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Where visitors are</h2></div>
                <WorldMap locations={byLocation} />
              </div>
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Live feed</h2></div>
                <div className="rzp-feed">
                  {recentVisits.length === 0 && <p className="rzp-empty">No recent visits yet.</p>}
                  {recentVisits.map((r, i) => (
                    <div className="rzp-feed-row" key={i}>
                      <span className={`rzp-feed-dot ${r.isMangoInterest ? "rzp-feed-dot--mango" : ""}`} />
                      <div className="rzp-feed-main">
                        <span className="rzp-feed-path">{r.path}</span>
                        <span className="rzp-feed-loc">{[r.city, r.countryName].filter(Boolean).join(", ") || "Unknown"}</span>
                      </div>
                      <span className="rzp-feed-time">{timeAgo(r.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rzp-charts rzp-charts--split">
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Daily visits <span className="rzp-count">This week</span></h2></div>
                <BarTrend data={byDay} title="Daily visits" />
              </div>
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Returning visitors</h2></div>
                <StatusDonut
                  data={returningDonut}
                  unitLabel="visitors"
                  title="Returning vs one-time visitors"
                  emptyLabel="No visits recorded yet."
                />
              </div>
            </div>

            <div className="rzp-charts rzp-charts--split">
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Top pages</h2></div>
                <HBars data={topPages} />
              </div>
              <div className="rzp-panel rzp-chart-card">
                <div className="rzp-panel-head"><h2>Traffic sources</h2></div>
                <HBars data={topSources} />
              </div>
            </div>

            <div className="rzp-panel rzp-chart-card">
              <div className="rzp-panel-head"><h2>Peak hour <span className="rzp-count">Today</span></h2></div>
              <BarTrend data={byHour} title="Visits by hour" height={140} />
            </div>
          </>
        )}

        {tab === "categories" && (
          <div className="rzp-panel">
            <div className="rzp-panel-head">
              <h2>Shop page categories</h2>
            </div>
            <p className="rzp-panel-note">
              Turning a category off hides it everywhere on the Products page — its filter chip
              and its products in "All". It stays in the catalog and is still reachable by search
              or a direct link.
            </p>
            <div className="rzp-cat-list">
              {categoryRows.map((c) => (
                <div className="rzp-cat-row" key={c.id}>
                  <span className="rzp-cat-row__label">{c.label}</span>
                  <label className="rzp-switch">
                    <input
                      type="checkbox"
                      checked={c.enabled}
                      onChange={() => toggleCategory(c)}
                      disabled={busy}
                      aria-label={`Show ${c.label} on the shop page`}
                    />
                    <span className="rzp-switch__track"><span className="rzp-switch__thumb" /></span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
